import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { formatCurrency, eurosToCents, centsToEuros } from '@/lib/utils';

interface CostItem {
  key: string;
  label: string;
  value: number | null;  // In Cent
  placeholder?: string;
}

interface ExpandableCostInputProps {
  label: string;
  totalValue: number | null;  // Summe in Cent
  onTotalChange: (value: number | null) => void;
  items: CostItem[];
  onItemChange: (key: string, value: number | null) => void;
  showDetails?: boolean;
  onToggleDetails?: (show: boolean) => void;
  helpText?: string;
}

export function ExpandableCostInput({
  label,
  totalValue,
  onTotalChange,
  items,
  onItemChange,
  showDetails: controlledShowDetails,
  onToggleDetails,
  helpText,
}: ExpandableCostInputProps) {
  const [internalShowDetails, setInternalShowDetails] = useState(false);
  const showDetails = controlledShowDetails ?? internalShowDetails;

  // Summe aus Details berechnen
  const calculatedTotal = items.reduce((sum, item) => sum + (item.value || 0), 0);
  const hasDetailValues = items.some(item => item.value && item.value > 0);

  // Wenn Details ausgefüllt sind, zeige berechnete Summe
  const displayTotal = hasDetailValues ? calculatedTotal : totalValue;

  const handleToggle = () => {
    const newValue = !showDetails;
    if (onToggleDetails) {
      onToggleDetails(newValue);
    } else {
      setInternalShowDetails(newValue);
    }
  };

  const handleTotalChange = (euros: number) => {
    onTotalChange(eurosToCents(euros));
  };

  const handleItemValueChange = (key: string, euros: number) => {
    onItemChange(key, eurosToCents(euros));
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label>{label}</Label>
        <button
          type="button"
          onClick={handleToggle}
          className="flex items-center gap-1 text-xs text-foreground-muted hover:text-foreground transition-colors"
        >
          {showDetails ? (
            <>
              <ChevronUp className="w-3 h-3" />
              Weniger
            </>
          ) : (
            <>
              <ChevronDown className="w-3 h-3" />
              Details
            </>
          )}
        </button>
      </div>

      {/* Summen-Eingabe (nur wenn keine Details) */}
      {!showDetails && (
        <div className="relative">
          <Input
            type="number"
            step="100"
            value={centsToEuros(displayTotal) || ''}
            onChange={(e) => handleTotalChange(parseFloat(e.target.value) || 0)}
            placeholder="0"
            disabled={hasDetailValues}
          />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-muted text-sm">
            €
          </span>
        </div>
      )}

      {/* Berechnete Summe anzeigen wenn Details aktiv */}
      {showDetails && hasDetailValues && (
        <div className="flex justify-between items-center py-2 px-3 bg-background-subtle rounded-md">
          <span className="text-sm text-foreground-muted">Summe (berechnet)</span>
          <span className="font-medium">{formatCurrency(calculatedTotal)}</span>
        </div>
      )}

      {/* Detail-Eingaben */}
      <AnimatePresence>
        {showDetails && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="pt-2 space-y-3 border-l-2 border-border pl-4 ml-2">
              {items.map((item) => (
                <div key={item.key} className="space-y-1">
                  <Label className="text-xs text-foreground-muted">{item.label}</Label>
                  <div className="relative">
                    <Input
                      type="number"
                      step="100"
                      value={centsToEuros(item.value) || ''}
                      onChange={(e) => handleItemValueChange(item.key, parseFloat(e.target.value) || 0)}
                      placeholder={item.placeholder || '0'}
                      className="h-9 text-sm"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-muted text-xs">
                      €
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {helpText && (
        <p className="text-xs text-foreground-muted">{helpText}</p>
      )}
    </div>
  );
}
